import type { UserRoleType } from '~~/shared/types/dds/enums';

interface DemoUser {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: UserRoleType;
    clientId: string | null;
    techType: string | null;
}

const useDemoUser = () => useState<DemoUser | null>('demo-user', () => null);

export function useDemoMode() {
    const demoUser = useDemoUser();
    const authUser = useAuthUser();
    const roleCookie = useCookie<string | null>('__dds_demo_role');
    const userCookie = useCookie<string | null>('__dds_demo_user');

    const isDemoMode = computed(() => true);

    const currentRole = computed<UserRoleType | null>(() =>
        demoUser.value?.role ?? (roleCookie.value as UserRoleType | null)
    );

    const currentUser = computed(() => demoUser.value);

    async function switchRole(userId: string) {
        const { data } = await $fetch<{ success: boolean; data: DemoUser }>('/api/demo/role', {
            method: 'POST',
            body: { userId }
        });

        demoUser.value = data;
        authUser.value = { id: data.id, email: data.email };
        roleCookie.value = data.role;
        userCookie.value = data.id;

        return data;
    }

    async function resetStore() {
        await $fetch('/api/demo/reset', { method: 'POST' });
    }

    async function logout() {
        demoUser.value = null;
        authUser.value = null;
        roleCookie.value = null;
        userCookie.value = null;
    }

    const roleCapabilities = computed(() => {
        const role = currentRole.value;
        if (!role) return {};

        return {
            canViewDashboard: ['admin', 'dispatcher'].includes(role),
            canViewMap: ['admin', 'dispatcher', 'super_client'].includes(role),
            canManageOrders: ['admin', 'dispatcher', 'client_csr'].includes(role),
            canAssignTechs: ['admin', 'dispatcher'].includes(role),
            canViewTechRoute: ['admin', 'dispatcher', 'technician'].includes(role),
            canViewAdminConfig: role === 'admin',
            canSubmitOrders: ['admin', 'dispatcher', 'client_csr'].includes(role),
            canViewOwnOrders: ['client_csr', 'super_client'].includes(role),
            canViewAllClients: ['admin', 'dispatcher'].includes(role)
        };
    });

    const defaultRoute = computed(() => {
        switch (currentRole.value) {
            case 'admin':
            case 'dispatcher':
                return '/app/dashboard';
            case 'technician':
                return `/app/techs/${currentUser.value?.id}/route`;
            case 'client_csr':
                return '/app/client/orders';
            case 'super_client':
                return '/app/map';
            default:
                return '/app/dashboard';
        }
    });

    return {
        isDemoMode,
        currentRole,
        currentUser,
        switchRole,
        resetStore,
        logout,
        roleCapabilities,
        defaultRoute
    };
}
